import React, { useState } from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { FaShieldAlt, FaHospital, FaUserMd, FaMapMarkerAlt } from "react-icons/fa";

const FAQItem = ({ q, a, i }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden">
      <button onClick={() => setOpen(!open)} className="w-full text-left px-5 py-4 font-semibold flex justify-between">
        <span>{q}</span>
        <span className={`transform transition ${open ? "rotate-45" : ""}`}>+</span>
      </button>
      {open && <div id={`faq-${i}`} className="px-5 pb-5 text-gray-600">{a}</div>}
    </div>
  );
};

export default function MedicareSupplements() {
  return (
    <>
      <Helmet>
        <title>Medicare Supplements (Medigap) | Your Choice Associates</title>
        <meta
          name="description"
          content="Medigap helps cover deductibles, copays, and coinsurance left by Original Medicare. Compare plans like G and N and keep nationwide access."
        />
      </Helmet>
      <Navbar />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0b3a70] via-[#16528f] to-[#2b76c2]" />
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="relative max-w-7xl mx-auto px-6 md:px-8 pt-20 pb-16 text-white"
        >
          <span className="inline-block text-xs font-bold tracking-widest uppercase bg-white/10 backdrop-blur px-3 py-1 rounded-full">
            Medicare Supplement Insurance (Medigap)
          </span>
          <h1 className="text-3xl md:text-5xl font-extrabold mt-4">Predictable costs. Broad access.</h1>
          <p className="mt-4 text-white/90 max-w-2xl">
            Fill the “gaps” of Original Medicare and see any provider nationwide who accepts Medicare — no referrals, no networks.
          </p>
        </motion.div>
      </section>

      {/* Benefits */}
      <section className="max-w-7xl mx-auto px-6 md:px-8 py-16">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            { icon: <FaShieldAlt />, title: "Fewer surprises", desc: "Plans like G & N minimize out‑of‑pocket costs." },
            { icon: <FaHospital />, title: "Any Medicare provider", desc: "Use doctors and hospitals nationwide that accept Medicare." },
            { icon: <FaUserMd />, title: "No referrals", desc: "Go directly to specialists without gatekeeping." },
            { icon: <FaMapMarkerAlt />, title: "Great for travelers", desc: "Consistent coverage across state lines." },
          ].map((c, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 8 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.35, delay: i * 0.05 }}
              className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100"
            >
              <div className="text-2xl">{c.icon}</div>
              <h3 className="mt-4 font-bold text-lg">{c.title}</h3>
              <p className="mt-2 text-gray-600">{c.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Plan overview */}
      <section className="bg-gray-50 py-16">
        <div className="max-w-6xl mx-auto px-6 md:px-8">
          <h2 className="text-2xl md:text-3xl font-bold mb-6">Popular plans at a glance</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm">
              <h3 className="font-semibold text-lg">Plan G</h3>
              <ul className="mt-3 space-y-2 text-gray-700 list-disc pl-5">
                <li>Covers all gaps except Part B deductible.</li>
                <li>Very predictable costs after small annual deductible.</li>
                <li>Great for frequent care users.</li>
              </ul>
            </div>
            <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm">
              <h3 className="font-semibold text-lg">Plan N</h3>
              <ul className="mt-3 space-y-2 text-gray-700 list-disc pl-5">
                <li>Lower premium than Plan G.</li>
                <li>Small copays at office/ER; doesn’t cover Part B excess charges.</li>
                <li>Popular balance of premium and protection.</li>
              </ul>
            </div>
          </div>
          <p className="text-gray-600 mt-4 text-sm">
            *Availability and rates vary by age, tobacco status, and location.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="max-w-5xl mx-auto px-6 md:px-8 py-16">
        <h2 className="text-2xl md:text-3xl font-bold mb-6">Medigap FAQs</h2>
        <div className="space-y-3">
          {[
            { q: "When is the best time to buy Medigap?", a: "During your 6‑month Medigap Open Enrollment Period (starts when Part B begins). Underwriting may apply afterward in many states." },
            { q: "Do Medigap plans include prescriptions?", a: "No. Pair Medigap with a standalone Part D plan for drug coverage." },
            { q: "Can I use any doctor?", a: "Yes—any provider that accepts Medicare nationwide, no referrals needed." },
          ].map((f, i) => <FAQItem key={i} i={i} {...f} />)}
        </div>
      </section>

      {/* CTA */}
      <section className="px-6 md:px-8 pb-20">
        <div className="max-w-7xl mx-auto">
          <div className="bg-[#002b5c] text-white rounded-3xl p-8 md:p-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6 shadow-lg">
            <div>
              <h3 className="text-2xl md:text-3xl font-extrabold">See rates for Plan G & N</h3>
              <p className="mt-2 text-white/90">We’ll compare multiple carriers and lock in the best fit for your needs.</p>
            </div>
            <a href="/start" className="inline-flex items-center font-semibold bg-white text-[#002b5c] px-5 py-3 rounded-xl hover:opacity-90 transition">
              Get Quotes
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
}
